import { BusinessCard, AppSettings, GitHubFile, ApiResponse } from '../types';

const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;
const GITHUB_USERNAME = process.env.REACT_APP_GITHUB_USERNAME;
const GITHUB_REPO = process.env.REACT_APP_GITHUB_REPO;

const BASE_URL = `https://api.github.com/repos/${GITHUB_USERNAME}/${GITHUB_REPO}`;

class GitHubAPI {
  private headers = {
    'Authorization': `token ${GITHUB_TOKEN}`,
    'Accept': 'application/vnd.github.v3+json',
    'Content-Type': 'application/json',
  };

  // Helper method to handle API responses
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.message || `HTTP error! status: ${response.status}`,
      };
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  }

  // Get file content from GitHub
  async getFile(path: string): Promise<ApiResponse<string>> {
    try {
      const response = await fetch(`${BASE_URL}/contents/${path}`, {
        headers: this.headers,
      });

      const result = await this.handleResponse<GitHubFile>(response);
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch file',
        };
      }

      // Decode base64 content
      const content = atob(result.data.content || '');
      return {
        success: true,
        data: content,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Update file content on GitHub
  async updateFile(path: string, content: string, message: string, sha?: string): Promise<ApiResponse<GitHubFile>> {
    try {
      let fileSha = sha;
      
      // If SHA is not provided, try to get it from the existing file
      if (!fileSha) {
        try {
          const response = await fetch(`${BASE_URL}/contents/${path}`, {
            headers: this.headers,
          });
          
          if (response.ok) {
            const fileInfo = await response.json();
            fileSha = fileInfo.sha;
          }
        } catch (error) {
          // If we can't get the SHA, it might be a new file, so we'll try without it
          console.warn('Could not get file SHA, attempting to create new file');
        }
      }

      const body = {
        message,
        content: btoa(content),
        ...(fileSha && { sha: fileSha }),
      };

      const response = await fetch(`${BASE_URL}/contents/${path}`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(body),
      });

      return await this.handleResponse<GitHubFile>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Delete file from GitHub
  async deleteFile(path: string, sha: string, message: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${BASE_URL}/contents/${path}`, {
        method: 'DELETE',
        headers: this.headers,
        body: JSON.stringify({
          message,
          sha,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.message || `HTTP error! status: ${response.status}`,
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Upload file to GitHub
  async uploadFile(path: string, content: string, message: string): Promise<ApiResponse<GitHubFile>> {
    try {
      // For new files, we don't need SHA
      const body = {
        message,
        content: btoa(content),
      };

      const response = await fetch(`${BASE_URL}/contents/${path}`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(body),
      });

      return await this.handleResponse<GitHubFile>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Get all cards
  async fetchCards(): Promise<ApiResponse<{ cards: BusinessCard[]; settings: AppSettings }>> {
    try {
      const result = await this.getFile('data/cards.json');
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch cards',
        };
      }

      const data = JSON.parse(result.data);
      
      // Ensure the data structure is complete
      if (!data.cards) {
        data.cards = [];
      }
      if (!data.settings) {
        data.settings = { mainCard: '', companyName: '', companyLogo: '' };
      }
      
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to parse cards data',
      };
    }
  }

  // Get specific card
  async fetchCard(slug: string): Promise<ApiResponse<BusinessCard>> {
    try {
      const result = await this.fetchCards();
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch cards',
        };
      }

      const card = result.data.cards.find(c => c.slug === slug);
      if (!card) {
        return {
          success: false,
          error: 'Card not found',
        };
      }

      return {
        success: true,
        data: card,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch card',
      };
    }
  }

  // Create new card
  async createCard(cardData: BusinessCard): Promise<ApiResponse<BusinessCard>> {
    try {
      const result = await this.fetchCards();
      if (!result.success) {
        return {
          success: false,
          error: result.error || 'Failed to fetch existing cards',
        };
      }

      const data = result.data || { cards: [], settings: { mainCard: '', companyName: '', companyLogo: '' } };
      
      // Ensure settings object exists
      if (!data.settings) {
        data.settings = { mainCard: '', companyName: '', companyLogo: '' };
      }
      
      // Check if card with same slug exists
      if (data.cards.some(card => card.slug === cardData.slug)) {
        return {
          success: false,
          error: 'Card with this slug already exists',
        };
      }

      // Add new card
      data.cards.push(cardData);

      // Update main card if needed
      if (cardData.isMainCard) {
        data.settings.mainCard = cardData.slug;
        data.cards.forEach(card => {
          if (card.slug !== cardData.slug) {
            card.isMainCard = false;
          }
        });
      }

      // Save to GitHub
      const saveResult = await this.updateFile(
        'data/cards.json',
        JSON.stringify(data, null, 2),
        `Add new card: ${cardData.name}`
      );

      if (!saveResult.success) {
        return {
          success: false,
          error: saveResult.error || 'Failed to save card',
        };
      }

      return {
        success: true,
        data: cardData,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create card',
      };
    }
  }

  // Update existing card
  async updateCard(slug: string, cardData: BusinessCard): Promise<ApiResponse<BusinessCard>> {
    try {
      const result = await this.fetchCards();
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch existing cards',
        };
      }

      const data = result.data;
      
      // Ensure settings object exists
      if (!data.settings) {
        data.settings = { mainCard: '', companyName: '', companyLogo: '' };
      }
      
      const cardIndex = data.cards.findIndex(card => card.slug === slug);
      
      if (cardIndex === -1) {
        return {
          success: false,
          error: 'Card not found',
        };
      }

      // Update card
      data.cards[cardIndex] = cardData;

      // Update main card if needed
      if (cardData.isMainCard) {
        data.settings.mainCard = cardData.slug;
        data.cards.forEach((card, index) => {
          if (index !== cardIndex) {
            card.isMainCard = false;
          }
        });
      }

      // Save to GitHub
      const saveResult = await this.updateFile(
        'data/cards.json',
        JSON.stringify(data, null, 2),
        `Update card: ${cardData.name}`
      );

      if (!saveResult.success) {
        return {
          success: false,
          error: saveResult.error || 'Failed to save card',
        };
      }

      return {
        success: true,
        data: cardData,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update card',
      };
    }
  }

  // Delete card
  async deleteCard(slug: string): Promise<ApiResponse<void>> {
    try {
      const result = await this.fetchCards();
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch existing cards',
        };
      }

      const data = result.data;
      
      // Ensure settings object exists
      if (!data.settings) {
        data.settings = { mainCard: '', companyName: '', companyLogo: '' };
      }
      
      const cardIndex = data.cards.findIndex(card => card.slug === slug);
      
      if (cardIndex === -1) {
        return {
          success: false,
          error: 'Card not found',
        };
      }

      const deletedCard = data.cards[cardIndex];
      
      // Remove card
      data.cards.splice(cardIndex, 1);

      // Update main card if deleted card was main
      if (deletedCard.isMainCard) {
        data.settings.mainCard = data.cards.length > 0 ? data.cards[0].slug : '';
        if (data.cards.length > 0) {
          data.cards[0].isMainCard = true;
        }
      }

      // Save to GitHub
      const saveResult = await this.updateFile(
        'data/cards.json',
        JSON.stringify(data, null, 2),
        `Delete card: ${deletedCard.name}`
      );

      if (!saveResult.success) {
        return {
          success: false,
          error: saveResult.error || 'Failed to save changes',
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete card',
      };
    }
  }

  // Upload image
  async uploadImage(file: File): Promise<ApiResponse<string>> {
    try {
      const reader = new FileReader();
      
      return new Promise((resolve) => {
        reader.onload = async () => {
          try {
            const base64Content = (reader.result as string).split(',')[1];
            const filename = `public/images/${Date.now()}-${file.name}`;
            
            const result = await this.uploadFile(
              filename,
              base64Content,
              `Upload image: ${file.name}`
            );

            if (!result.success) {
              resolve({
                success: false,
                error: result.error || 'Failed to upload image',
              });
              return;
            }

            resolve({
              success: true,
              data: `/${filename.replace('public/', '')}`,
            });
          } catch (error) {
            resolve({
              success: false,
              error: error instanceof Error ? error.message : 'Failed to process image',
            });
          }
        };

        reader.readAsDataURL(file);
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload image',
      };
    }
  }

  // Delete image
  async deleteImage(imagePath: string): Promise<ApiResponse<void>> {
    try {
      // Get file info to get SHA
      const filePath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
      const fullPath = `public/${filePath}`;
      
      const response = await fetch(`${BASE_URL}/contents/${fullPath}`, {
        headers: this.headers,
      });

      if (!response.ok) {
        return {
          success: false,
          error: `Failed to get file info: ${response.status}`,
        };
      }

      const fileInfo = await response.json();
      
      // Delete file
      const deleteResult = await this.deleteFile(
        fullPath,
        fileInfo.sha,
        `Delete image: ${filePath}`
      );

      return deleteResult;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete image',
      };
    }
  }
}

export const githubApi = new GitHubAPI(); 